import { useState } from "react";
import { ExternalLink, Play, Info } from "lucide-react";

/**
 * Parse a video URL into { type, src } for embedding, or null if unsupported.
 * Supported embed targets: YouTube, Vimeo, Facebook (via plugin iframe).
 * Instagram/TikTok/other → null (caller renders fallback button).
 */
function parseVideo(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const url = rawUrl.trim();
  if (!url) return null;
  let u;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\./, "").toLowerCase();

  // ---- YouTube -------------------------------------------------------------
  if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "youtube-nocookie.com" ||
    host === "youtu.be"
  ) {
    let id = "";
    let portrait = false;
    if (host === "youtu.be") {
      id = u.pathname.slice(1).split("/")[0];
    } else if (u.pathname.startsWith("/embed/")) {
      id = u.pathname.split("/")[2] || "";
    } else if (u.pathname.startsWith("/shorts/")) {
      id = u.pathname.split("/")[2] || "";
      portrait = true;
    } else if (u.pathname === "/watch") {
      id = u.searchParams.get("v") || "";
    }
    id = id.replace(/[^\w-]/g, "");
    if (id) {
      const start = u.searchParams.get("t") || u.searchParams.get("start");
      const startSec = start ? String(start).replace(/[^0-9]/g, "") : "";
      const params = new URLSearchParams({ rel: "0" });
      if (startSec) params.set("start", startSec);
      return {
        type: "youtube",
        src: `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`,
        portrait,
      };
    }
  }

  // ---- Vimeo ---------------------------------------------------------------
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const match = u.pathname.match(/\/(?:video\/)?(\d+)/);
    if (match) {
      return {
        type: "vimeo",
        src: `https://player.vimeo.com/video/${match[1]}?dnt=1`,
        portrait: false,
      };
    }
  }

  // ---- Facebook ------------------------------------------------------------
  // Facebook plugin iframe works for *public* videos without their SDK.
  // Reels are portrait 9:16 — flag them so we render in a narrower container.
  if (host.endsWith("facebook.com") || host === "fb.watch") {
    const portrait =
      u.pathname.startsWith("/reel/") ||
      u.pathname.startsWith("/share/r/") ||
      /\/reel\//.test(u.pathname);
    // Plugin width sweet-spot: 720 for landscape, 405 for portrait.
    const w = portrait ? 405 : 720;
    return {
      type: "facebook",
      src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        url
      )}&show_text=false&width=${w}`,
      portrait,
    };
  }

  return null;
}

/** Human-readable platform label, used in fallback button text. */
function platformLabel(url = "") {
  const low = url.toLowerCase();
  if (low.includes("youtu")) return "YouTube";
  if (low.includes("facebook") || low.includes("fb.watch")) return "Facebook";
  if (low.includes("instagram")) return "Instagram";
  if (low.includes("tiktok")) return "TikTok";
  if (low.includes("vimeo")) return "Vimeo";
  if (low.includes("twitter") || low.includes("x.com")) return "X / Twitter";
  return "extern sida";
}

/**
 * Render a video embed if we have a known iframe target, otherwise a tidy
 * fallback button. Click-to-load: the iframe is NOT loaded until the user
 * actively clicks "Visa video" — keeps the site free of third-party cookies
 * by default and respects GDPR/ePrivacy.
 */
export default function VideoEmbed({ url, className = "" }) {
  const [loaded, setLoaded] = useState(false);
  if (!url) return null;
  const parsed = parseVideo(url);
  const label = platformLabel(url);

  if (!parsed) {
    return (
      <FallbackButton
        url={url}
        label={label}
        className={className}
        testId="video-fallback"
      />
    );
  }

  return (
    <figure
      data-testid={`video-embed-${parsed.type}`}
      className={`${className}${parsed.portrait ? " max-w-[400px] mx-auto" : ""}`}
    >
      <div
        className="relative w-full overflow-hidden rounded-xl border border-[#E2E0D8] bg-[#1B2A1E] shadow-sm"
        style={{ paddingBottom: parsed.portrait ? "177.78%" : "56.25%" }}
      >
        {loaded ? (
          <iframe
            src={parsed.src}
            loading="lazy"
            title={`Inbäddad video från ${label}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            data-testid={`video-consent-${parsed.type}`}
            className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 text-white text-center px-6 py-8 bg-gradient-to-b from-[#1B2A1E]/95 to-[#0F1A12]/95 hover:from-[#243A2A]/95 hover:to-[#162018]/95 transition-colors group"
            aria-label={`Ladda och spela video från ${label}`}
          >
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D95D2E] group-hover:bg-[#B84B22] transition-colors shadow-lg">
              <Play className="w-7 h-7 fill-white text-white ml-1" />
            </span>
            <div>
              <div className="font-heading text-base sm:text-lg font-semibold">
                Visa video från {label}
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] sm:text-sm text-white/75 max-w-xs">
                <Info className="w-3.5 h-3.5 shrink-0" />
                <span>
                  Den här videon laddas från en extern tjänst och kan använda
                  cookies.
                </span>
              </div>
            </div>
          </button>
        )}
      </div>
      {parsed.type === "facebook" && (
        <figcaption className="mt-3 text-sm text-[#5A6258]">
          Visas inte videon?{" "}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="video-facebook-fallback-link"
            className="inline-flex items-center gap-1 text-[#D95D2E] font-semibold hover:underline"
          >
            Öppna på Facebook
            <ExternalLink className="w-3 h-3" />
          </a>
        </figcaption>
      )}
    </figure>
  );
}

function FallbackButton({ url, label, className = "", testId }) {
  return (
    <div className={className}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className="inline-flex items-center gap-2 bg-[#1B2A1E] hover:bg-[#283224] text-white font-semibold px-5 py-3 rounded-lg transition-colors"
      >
        <Play className="w-4 h-4" />
        Öppna video på {label}
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

export { parseVideo, platformLabel };
